import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Calendar, DollarSign, ShieldCheck, Star,
  ClipboardList, BadgeCheck, Sparkles, Smartphone, IdCard, Clock, ThumbsUp,
} from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";

export default function WorkWithUsPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const L = (en, es) => (locale === "es" ? es : en);

  const benefits = [
    { icon: Calendar, en: "Choose only the jobs that fit your schedule", es: "Elige solo los trabajos que se adaptan a tu agenda" },
    { icon: DollarSign, en: "Get paid for every cleaning — no middlemen", es: "Cobra por cada limpieza — sin intermediarios" },
    { icon: ShieldCheck, en: "Your customer info is private until you accept the job", es: "Los datos del cliente quedan privados hasta que aceptes el trabajo" },
    { icon: Star, en: "Build your rating and grow your client base", es: "Construye tu reputación y haz crecer tu base de clientes" },
  ];

  const steps = [
    { icon: ClipboardList, n: "1", t_en: "Apply", t_es: "Postúlate", d_en: "Fill out a quick application with your experience and a photo.", d_es: "Completa una postulación rápida con tu experiencia y una foto." },
    { icon: BadgeCheck, n: "2", t_en: "Get verified", t_es: "Verificación", d_en: "We review your profile and ID — usually within 48 hours.", d_es: "Revisamos tu perfil y tu identificación — normalmente en 48 horas." },
    { icon: Sparkles, n: "3", t_en: "Start cleaning", t_es: "Empieza a limpiar", d_en: "Once approved, log in and start picking up jobs in your area.", d_es: "Una vez aprobado, ingresa y empieza a tomar trabajos en tu zona." },
  ];

  const requirements = [
    { icon: Smartphone, en: "Smartphone with internet", es: "Smartphone con internet" },
    { icon: Calendar, en: "18+ years old", es: "Mayor de 18 años" },
    { icon: IdCard, en: "Valid ID", es: "Identificación válida" },
    { icon: Clock, en: "At least 1 year of cleaning experience", es: "Al menos 1 año de experiencia en limpieza" },
    { icon: ThumbsUp, en: "Professional attitude", es: "Actitud profesional" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="work-with-us-page">
      <SEO
        title={L("Work with us — Ever Clean Home", "Trabaja con nosotros — Ever Clean Home")}
        description={L("Join the Ever Clean Home cleaning team. Pick your jobs, set your schedule, get paid.",
          "Súmate al equipo de limpieza de Ever Clean Home. Elige tus trabajos, tu horario y cobra.")}
      />
      <Header />

      {/* Hero */}
      <section className="bg-[#0C1A1A] text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#0EA5A4] font-bold" data-testid="wwu-eyebrow">
            {L("CLEANERS — JOIN OUR TEAM", "LIMPIADORES — SÚMATE AL EQUIPO")}
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] max-w-3xl">
            {L("Pick your jobs. Set your schedule. Get paid for what you do best.",
              "Elige tus trabajos. Organiza tu horario. Cobra por lo que mejor haces.")}
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl">
            {L("Ever Clean Home connects you with homes that need cleaning in your area. Apply once, get verified, start working.",
              "Ever Clean Home te conecta con hogares que necesitan limpieza en tu zona. Postúlate una vez, verifícate y empieza a trabajar.")}
          </p>
          <Button onClick={() => navigate("/work-with-us/apply")}
            className="mt-9 h-12 px-7 rounded-xl bg-[#FACC15] text-[#0C1A1A] hover:bg-[#eab308] font-bold text-base"
            data-testid="wwu-hero-apply">
            {L("Apply now", "Postularme")} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Why work with us */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-20 w-full">
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">
          {L("Why work with us", "Por qué trabajar con nosotros")}
        </h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-start gap-4 rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid={`wwu-benefit-${i}`}>
              <span className="w-11 h-11 rounded-xl bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center shrink-0">
                <b.icon className="w-5 h-5" />
              </span>
              <p className="text-[#0C1A1A] font-medium pt-1.5">{L(b.en, b.es)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">{L("How it works", "Cómo funciona")}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-[#E5E7EB] p-6" data-testid={`wwu-step-${s.n}`}>
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-2xl bg-[#0EA5A4] text-white grid place-items-center"><s.icon className="w-6 h-6" /></span>
                  <span className="font-mono text-5xl font-extrabold text-[#E5E7EB]">{s.n}</span>
                </div>
                <h3 className="mt-4 font-display text-xl font-bold">{L(s.t_en, s.t_es)}</h3>
                <p className="mt-2 text-[#4B5563] text-sm leading-relaxed">{L(s.d_en, s.d_es)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you need */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-20 w-full">
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">{L("What you need", "Qué necesitas")}</h2>
        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {requirements.map((r, i) => (
            <li key={i} className="flex items-center gap-3 rounded-xl bg-white border border-[#E5E7EB] px-4 py-3.5" data-testid={`wwu-req-${i}`}>
              <r.icon className="w-5 h-5 text-[#0EA5A4] shrink-0" />
              <span className="text-[#0C1A1A] font-medium">{L(r.en, r.es)}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0EA5A4]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-white">
            {L("Ready to get started?", "¿Listo para empezar?")}
          </h2>
          <p className="mt-3 text-white/85 max-w-xl mx-auto">
            {L("Apply today and join a team that values your work.", "Postúlate hoy y súmate a un equipo que valora tu trabajo.")}
          </p>
          <Button onClick={() => navigate("/work-with-us/apply")}
            className="mt-7 h-12 px-7 rounded-xl bg-white text-[#0F766E] hover:bg-white/90 font-bold text-base"
            data-testid="wwu-footer-apply">
            {L("Apply now", "Postularme")} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <div className="mt-5 text-white/80 text-sm">
            {L("Already approved?", "¿Ya estás aprobado?")}{" "}
            <Link to="/cleaner/login" className="underline font-semibold" data-testid="wwu-login-link">
              {L("Log in here", "Ingresa aquí")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
